import { zodResolver } from '@hookform/resolvers/zod';
import { depoimentoSchema, DEPOIMENTO_PADRAO, type Depoimento } from '@plane/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Field } from '@/components/field';
import { useDepoimento, useSaveDepoimento } from '@/modules/site-content/hooks/use-conteudo';
import { ApiRequestError } from '@/services/http-client';

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function DepoimentoPage() {
  const consulta = useDepoimento();
  const salvar = useSaveDepoimento();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? DEPOIMENTO_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(depoimento) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(depoimento)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Depoimento;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (depoimento: Depoimento) => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Depoimento>({
    resolver: zodResolver(depoimentoSchema),
    defaultValues: inicial,
  });

  const texto = watch('texto');
  const escondida = texto.trim() === '';

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit(aoEnviar)(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Depoimento</h1>
        <p className="mt-3 text-sm text-ink-soft">A faixa verde entre o processo e o showroom.</p>
      </div>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          O texto abaixo <strong>é de demonstração</strong>: a Marcela, o prédio e a obra não
          existem. Publicar como real um elogio que ninguém escreveu é inventar cliente — troque por
          um depoimento de verdade, com autorização de quem falou, ou apague o texto para esconder a
          seção.
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="texto" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          O que a pessoa disse
        </label>
        <textarea
          id="texto"
          rows={5}
          aria-invalid={errors.texto ? true : undefined}
          aria-describedby="texto-ajuda"
          className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
          {...register('texto')}
        />
        <p id="texto-ajuda" className="text-xs text-ink-soft">
          Vazio esconde a seção inteira do site.
        </p>
        {errors.texto && (
          <p role="alert" className="text-xs text-brass">
            {errors.texto.message}
          </p>
        )}
      </div>

      {escondida ? (
        <p role="status" className="border-l-2 border-clay pl-3 text-sm text-ink-soft">
          Sem texto, a seção não aparece no site. É a saída enquanto não houver um depoimento real.
        </p>
      ) : (
        <>
          <Field
            id="autor"
            label="Quem falou"
            placeholder="Marcela A."
            error={errors.autor?.message}
            {...register('autor')}
          />

          <div>
            <Field
              id="contexto"
              label="Contexto"
              placeholder="Suíte e closet · Ed. Ipê Amarelo, Cascavel"
              error={errors.contexto?.message}
              {...register('contexto')}
            />
            <p className="mt-1.5 text-xs text-ink-soft">
              O que foi feito e onde. Pode ficar vazio.
            </p>
          </div>
        </>
      )}

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}
